/*******************************************************************************
 * Copyright (c) 2025 Bula Technologies
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 *  Version: 1.0.0
 *
 *******************************************************************************/
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Divider,
  alpha,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SportsGolfRoundedIcon from "@mui/icons-material/SportsGolfRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  callAcceptrandomusersforbet,
  calllistbetsfortesting,
  callGetcompletedbettestsummary,
} from "../../../../services/bet/BetController";
import { formatFirestoreTimestamp } from "../../../../utils/formatTimestampToDate";
import BackButton from "../../../../components/shared/Buttons/BackButton";
import CompletedBetSummaryModal from "./views/CompletedBetSummaryModal";

const AutomatedTestingPage = () => {
  const [loading, setLoading] = useState(false);
  const [betsLoading, setBetsLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [bets, setBets] = useState([]);
  const [selectedBetId, setSelectedBetId] = useState("");
  const [targetRandomAccepts, setTargetRandomAccepts] = useState(31);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [betSummary, setBetSummary] = useState(null);

  const selectedBet = useMemo(
    () => bets.find((bet) => bet.betID === selectedBetId) || null,
    [bets, selectedBetId]
  );

  const fetchListbetsfortesting = async () => {
    setBetsLoading(true);
    setError("");

    try {
      const response = await calllistbetsfortesting({
        limit: 100,
      });

      const fetchedBets = response?.bets || [];
      setBets(fetchedBets);

      if (fetchedBets.length > 0 && !selectedBetId) {
        setSelectedBetId(fetchedBets[0].betID);
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to load bets.");
    } finally {
      setBetsLoading(false);
    }
  };

  useEffect(() => {
    fetchListbetsfortesting();
  }, []);

  const handleAcceptUsers = async () => {
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await callAcceptrandomusersforbet({
        betID: selectedBetId,
        targetRandomAccepts: Number(targetRandomAccepts),
      });

      setResult(response);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to accept random users.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewSummary = async () => {
    if (!selectedBetId) return;

    setSummaryLoading(true);
    setError("");

    try {
      const response = await callGetcompletedbettestsummary({
        betID: selectedBetId,
      });

      setBetSummary(response);
      setSummaryModalOpen(true);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to load completed bet summary.");
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <BackButton pageTitle="Automated Testing" />
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
              border: "1px solid",
              borderColor: alpha("#ff5a3d", 0.12),
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    Configure Test Run
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.8}>
                    Select a bet and choose how many users should be accepted automatically.
                  </Typography>
                </Box>

                {error && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: "rgba(244, 67, 54, 0.12)",
                      border: "1px solid rgba(244, 67, 54, 0.35)",
                      color: "#ffb3b3",
                    }}
                  >
                    <Typography variant="body2">{error}</Typography>
                  </Box>
                )}

                <FormControl fullWidth disabled={betsLoading || loading || summaryLoading}>
                  <InputLabel id="bet-select-label">Select Bet</InputLabel>
                  <Select
                    labelId="bet-select-label"
                    value={selectedBetId}
                    label="Select Bet"
                    onChange={(e) => setSelectedBetId(e.target.value)}
                    sx={{
                      borderRadius: 3,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 3,
                          mt: 1,
                        },
                      },
                    }}
                  >
                    {bets.map((bet) => (
                      <MenuItem key={bet.betID} value={bet.betID}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ width: "100%", gap: 2 }}
                        >
                          <Typography fontWeight={600}>
                            {bet.betName || "-"}  {"   "} {bet.betStatus || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {bet.createdDate
                              ? formatFirestoreTimestamp(bet.createdDate)
                              : "-"}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      label="Target Random Accepts"
                      type="number"
                      fullWidth
                      value={targetRandomAccepts}
                      onChange={(e) => setTargetRandomAccepts(e.target.value)}
                      disabled={loading}
                      helperText="How many invited users to randomly accept in this run."
                      InputProps={{
                        sx: { borderRadius: 3 },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: "rgba(255, 90, 61, 0.06)",
                        border: "1px solid rgba(255, 90, 61, 0.2)",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        • <strong>Target Random Accepts</strong> = how many users this script will try to accept now.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    onClick={fetchListbetsfortesting}
                    disabled={betsLoading || loading || summaryLoading}
                    startIcon={
                      betsLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <RefreshRoundedIcon />
                      )
                    }
                    sx={{
                      borderRadius: "999px",
                      px: 3,
                      py: 1.3,
                      borderColor: alpha("#ff5a3d", 0.55),
                      color: "#ff5a3d",
                      "&:hover": {
                        borderColor: "#ff5a3d",
                        bgcolor: alpha("#ff5a3d", 0.06),
                      },
                    }}
                  >
                    Refresh Bets
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleAcceptUsers}
                    disabled={!selectedBetId || loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PlayArrowRoundedIcon />
                      )
                    }
                    sx={{
                      borderRadius: "999px",
                      px: 3.5,
                      py: 1.3,
                      fontWeight: 700,
                      background: "linear-gradient(135deg, #ff6a4d 0%, #ff4b2b 100%)",
                      boxShadow: "0 10px 25px rgba(255,90,61,0.35)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #ff7256 0%, #ff4b2b 100%)",
                      },
                    }}
                  >
                    Accept Users
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleViewSummary}
                    disabled={!selectedBetId || summaryLoading}
                    startIcon={
                      summaryLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <VisibilityRoundedIcon />
                      )
                    }
                    sx={{
                      borderRadius: "999px",
                      px: 3,
                      py: 1.3,
                    }}
                  >
                    View Summary
                  </Button>
                </Stack>

                {selectedBet && (
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      background:
                        "linear-gradient(180deg, rgba(255,90,61,0.07) 0%, rgba(255,90,61,0.02) 100%)",
                      border: "1px solid",
                      borderColor: alpha("#ff5a3d", 0.18),
                    }}
                  >
                    <Stack spacing={1.2}>
                      <Typography variant="subtitle1" fontWeight={800}>
                        Selected Bet
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <SportsGolfRoundedIcon sx={{ color: "#ff5a3d", fontSize: 18 }} />
                        <Typography variant="body2">
                          <strong>Name:</strong> {selectedBet.betName || "Untitled Bet"}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTimeRoundedIcon sx={{ color: "#ff5a3d", fontSize: 18 }} />
                        <Typography variant="body2">
                          <strong>Created:</strong>{" "}
                          {selectedBet.createdDate
                            ? formatFirestoreTimestamp(selectedBet.createdDate)
                            : "-"}
                        </Typography>
                      </Stack>

                      <Typography
                        variant="body2"
                        sx={{
                          wordBreak: "break-all",
                          color: "text.secondary",
                          pt: 0.5,
                        }}
                      >
                        <strong>Bet ID:</strong> {selectedBet.betID}
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
              border: "1px solid",
              borderColor: alpha("#ff5a3d", 0.12),
              minHeight: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    Result
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.8}>
                    Review the automated acceptance outcome and any failed users.
                  </Typography>
                </Box>

                <Divider />

                {!result ? (
                  <Box
                    sx={{
                      minHeight: 260,
                      borderRadius: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      border: "1px dashed",
                      borderColor: alpha("#999", 0.35),
                      bgcolor: alpha("#fff", 0.03),
                    }}
                  >
                    <SportsGolfRoundedIcon
                      sx={{ fontSize: 42, color: "text.secondary", mb: 1 }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      No result yet.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Run a test to see the response here.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      flexWrap="wrap"
                    >
                      <Chip
                        icon={
                          result.success ? (
                            <CheckCircleRoundedIcon />
                          ) : (
                            <ErrorOutlineRoundedIcon />
                          )
                        }
                        label={result.success ? "Completed" : "Completed with Errors"}
                        color={result.success ? "success" : "warning"}
                        sx={{ borderRadius: "999px", fontWeight: 700 }}
                      />

                      <Chip
                        label={`Accepted Before: ${result.acceptedBefore ?? 0}`}
                        sx={{ borderRadius: "999px" }}
                      />
                      <Chip
                        label={`Accepted After: ${result.acceptedAfter ?? 0}`}
                        sx={{ borderRadius: "999px" }}
                      />
                      <Chip
                        label={`Success: ${result.successCount ?? 0}`}
                        color="success"
                        variant="outlined"
                        sx={{ borderRadius: "999px" }}
                      />
                      <Chip
                        label={`Failed: ${result.failedCount ?? 0}`}
                        color={(result.failedCount ?? 0) > 0 ? "error" : "default"}
                        variant="outlined"
                        sx={{ borderRadius: "999px" }}
                      />
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            bgcolor: alpha("#4caf50", 0.08),
                            border: "1px solid",
                            borderColor: alpha("#4caf50", 0.18),
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Bet Status After
                          </Typography>
                          <Typography variant="h6" fontWeight={800}>
                            {result.betStatusAfter || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            bgcolor: alpha("#ff9800", 0.08),
                            border: "1px solid",
                            borderColor: alpha("#ff9800", 0.18),
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Bet Pool After
                          </Typography>
                          <Typography variant="h6" fontWeight={800}>
                            {result.betPoolAfter ?? "-"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box
                      sx={{
                        p: 2.2,
                        borderRadius: 3,
                        bgcolor: "#0f1117",
                        border: "1px solid",
                        borderColor: alpha("#ffffff", 0.08),
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontFamily: "monospace",
                        fontSize: 13.5,
                        color: "#d8e1ff",
                        maxHeight: 420,
                        overflowY: "auto",
                      }}
                    >
                      {JSON.stringify(result, null, 2)}
                    </Box>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <CompletedBetSummaryModal
        open={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        betSummary={betSummary}
      />
    </Box>
  );
};

export default AutomatedTestingPage;